import { ReactElement, useCallback } from "react";
import { AppType } from "client/Api/Apps/common";
import { never } from "client_legacy/ndaw/errors";
import {
	navigateToExcelApp,
	navigateToOneNoteApp,
	navigateToPowerPointApp,
	navigateToWordApp
} from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "./useRegisterTileUsage";
import { useRenderManager } from "../useRenderManager";
import { OneByOneTileContent } from "./OneByOneTileContent";
import { TwoByTwoTileContent } from "./TwoByTwoTileContent";
import { TileRenderState } from "../RenderState/common";
import { useBaseNavigation } from "./useInvokeNavigation";
import { TileContextProvider } from "../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

type OfficeTileAppTypes =
	| AppType.Office365Word
	| AppType.Office365Excel
	| AppType.Office365PowerPoint
	| AppType.Office365OneNote;

export type OfficeTileContentProps = {
	renderState: { [key in OfficeTileAppTypes]: TileRenderState<key>; }[OfficeTileAppTypes];
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const OfficeTileContent = (props: OfficeTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useOfficeTileNavigation(
		renderState,
		onEdit
	);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<TwoByTwoTileContent
				onClick={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
OfficeTileContent.displayName = "OfficeTileContent";

export function useOfficeTileNavigation(
	renderStateIsh: Pick<TileRenderState<OfficeTileAppTypes>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const {
		type
	} = renderStateIsh.data;

	const registerTileUsage: (() => void) = useRegisterTileUsage(renderStateIsh);

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				registerTileUsage();

				switch (type) {
					case AppType.Office365Word:
						navigateToWordApp();
						break;
					case AppType.Office365Excel:
						navigateToExcelApp();
						break;
					case AppType.Office365PowerPoint:
						navigateToPowerPointApp();
						break;
					case AppType.Office365OneNote:
						navigateToOneNoteApp();
						break;
					default:
						never(type);
				}
			},
			[registerTileUsage, type]
		)
	);
}
